import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {startOfToday} from 'date-fns'

import {
    type AssignmentStripDateRangeOptions,
    DEFAULT_ASSIGNMENT_STRIP_DATE_RANGE,
    formatAssignmentStripDateRange,
} from 'src/assignments/assignment-strip-utils'

import {
    archiveWorker,
    createWorker,
    getWorker,
    listWorkerAssignments,
    listWorkers,
    listWorkerTimeEntries,
    unarchiveWorker,
    updateWorker,
} from './api'
import {workerKeys} from './query-keys'
import type {CreateWorkerInput, UpdateWorkerInput} from './schemas'

type UseWorkersOptions = {
    enabled?: boolean
    workerTypeId?: string
    includeArchived?: boolean
}

export function useWorkers(options?: UseWorkersOptions) {
    const workerTypeId = options?.workerTypeId
    const includeArchived = options?.includeArchived

    return useQuery({
        queryKey: workerKeys.list(workerTypeId, includeArchived),
        queryFn: () => listWorkers({workerTypeId, includeArchived}),
        enabled: options?.enabled ?? true,
    })
}

export function useWorker(workerId: string, options?: {enabled?: boolean}) {
    return useQuery({
        queryKey: workerKeys.detail(workerId),
        queryFn: () => getWorker(workerId),
        enabled: (options?.enabled ?? true) && Boolean(workerId),
    })
}

export function useCreateWorker() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (input: CreateWorkerInput) => createWorker(input),
        onSuccess: () => {
            void queryClient.invalidateQueries({queryKey: workerKeys.all})
        },
    })
}

export function useUpdateWorker() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: ({id, input}: {id: string; input: UpdateWorkerInput}) =>
            updateWorker(id, input),
        onSuccess: (_data, variables) => {
            void queryClient.invalidateQueries({queryKey: workerKeys.all})
            void queryClient.invalidateQueries({queryKey: workerKeys.detail(variables.id)})
        },
    })
}

export function useArchiveWorker() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (id: string) => archiveWorker(id),
        onSuccess: (_data, id) => {
            void queryClient.invalidateQueries({queryKey: workerKeys.all})
            void queryClient.invalidateQueries({queryKey: workerKeys.detail(id)})
        },
    })
}

export function useUnarchiveWorker() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (id: string) => unarchiveWorker(id),
        onSuccess: (_data, id) => {
            void queryClient.invalidateQueries({queryKey: workerKeys.all})
            void queryClient.invalidateQueries({queryKey: workerKeys.detail(id)})
        },
    })
}

type UseWorkerAssignmentsOptions = {
    enabled?: boolean
    referenceDate?: Date
    dateRange?: AssignmentStripDateRangeOptions
}

export function getWorkerAssignmentDateRange(
    referenceDate: Date = startOfToday(),
    dateRange: AssignmentStripDateRangeOptions = DEFAULT_ASSIGNMENT_STRIP_DATE_RANGE,
) {
    return formatAssignmentStripDateRange(referenceDate, dateRange)
}

export function useWorkerAssignments(workerId: string, options?: UseWorkerAssignmentsOptions) {
    const {
        enabled = true,
        referenceDate: referenceDateOption,
        dateRange = DEFAULT_ASSIGNMENT_STRIP_DATE_RANGE,
    } = options ?? {}
    const {dateFrom, dateTo, referenceDate} = getWorkerAssignmentDateRange(
        referenceDateOption ?? startOfToday(),
        dateRange,
    )

    const query = useQuery({
        queryKey: workerKeys.assignments(workerId, dateFrom, dateTo),
        queryFn: () => listWorkerAssignments(workerId, {dateFrom, dateTo}),
        enabled: enabled && Boolean(workerId),
    })

    return {...query, referenceDate, dateRange}
}

const DEFAULT_TIME_ENTRIES_LIMIT = 20

export function useWorkerTimeEntries(
    workerId: string,
    options?: {enabled?: boolean; limit?: number},
) {
    const limit = options?.limit ?? DEFAULT_TIME_ENTRIES_LIMIT

    return useQuery({
        queryKey: workerKeys.timeEntries(workerId, limit),
        queryFn: () => listWorkerTimeEntries(workerId, {limit}),
        enabled: (options?.enabled ?? true) && Boolean(workerId),
    })
}
